import { useQuery } from '@tanstack/react-query';
import { Address, TokenId } from 'nftx.js';
import { useNftx } from '@nftx/react';
import type { SwapType } from '@/domain/swap';
import useInfrastructure from '../../useInfrastructure';
import { QueryKey } from '../../queryKeys';
import { useSettings } from '@/presentation/context/settings';

const useBuyAmount = (
  {
    sellAmount,
    sellTokenIds,
    sellVaultId,
    sellType,
    sellToken,
    buyType,
    buyToken,
    buyTokenIds,
    userAddress,
  }: {
    userAddress: Address | undefined;
    sellType: SwapType;
    sellToken: Address;
    sellVaultId: string;
    sellAmount: `${number}`;
    sellTokenIds: [TokenId, number][];
    buyType: SwapType;
    buyToken: Address;
    buyTokenIds: [TokenId, number][];
  },
  opts?: { enabled?: boolean; refetchInterval?: number; staleTime?: number }
) => {
  const { network } = useNftx();
  const {
    swap: { fetchBuyAmount },
  } = useInfrastructure();
  const [settings] = useSettings();
  const slippagePercentage = Number(settings.slippage);

  const enabled = !!(
    sellType &&
    (sellType !== 'NFT' ? sellAmount : true) &&
    sellAmount[0] !== '-' &&
    opts?.enabled !== false
  );

  const query = useQuery(
    [
      QueryKey.BUY_AMOUNT,
      network,
      sellAmount,
      `${sellTokenIds}`,
      sellType,
      sellToken,
      sellVaultId,
      buyType,
      buyToken,
      `${buyTokenIds}`,
      userAddress,
      slippagePercentage,
    ],
    async () => {
      return fetchBuyAmount({
        network,
        buyToken,
        buyType,
        buyTokenIds,
        sellAmount,
        sellToken,
        sellTokenIds,
        sellType,
        sellVaultId,
        userAddress,
        slippagePercentage,
      });
    },
    {
      keepPreviousData: false,
      refetchInterval: opts?.refetchInterval ?? 10000,
      staleTime: opts?.staleTime ?? 10000,
      enabled,
    }
  );

  const status = query.isRefetching ? 'loading' : query.status;

  return { ...query, status };
};

export default useBuyAmount;
